import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogDismiss,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Skeleton,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from '@barfacil/barfacil-ui'
import { ImageIcon, Search } from 'lucide-react'
import { startTransition, useEffect, useMemo, useRef, useState } from 'react'

import {
  Image,
  PriceListImageGalleryParams,
  UpdatePriceListProductBody,
} from '@/api/_schemas/management'
import {
  AddImageButton,
  ImportImage,
} from '@/app/panel/management/price-list/_components/add-image-button'
import { useImageGalleryInfinite } from '@/app/panel/management/price-list/_hooks/use-image-gallery'
import { useUpdatePriceListProduct } from '@/app/panel/management/price-list/_hooks/use-update-price-list-product'
import { Spinner as CenteredSpinner } from '@/components/ui/spinner'
import { screenSize } from '@/config/screens'
import { useWindowSize } from '@/hooks/use-window-size'
import { cn, removePropsFromObject } from '@/lib/utils'

type PriceListProductImageDialogProps = Pick<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'className'
> & {
  product?: Partial<UpdatePriceListProductBody & { id: number }>
  priceListId?: number
  id?: number
  image: Image
  isInactive?: boolean
  onSave?: (image: Image) => void
}

type GalleryLoadingProps = {
  totalItems: number
}

function GalleryLoading({ totalItems }: GalleryLoadingProps) {
  const { width } = useWindowSize()

  const itemsPerLine = width < screenSize.SM ? 3 : 7

  const length = itemsPerLine - (totalItems % itemsPerLine) + itemsPerLine * 2

  return (
    <>
      {Array.from({ length }).map((_, index) => (
        <Skeleton
          key={index}
          className="size-16 rounded border border-gray-100"
        />
      ))}
    </>
  )
}

export function PriceListProductImageDialog({
  className,
  image,
  product,
  id,
  priceListId,
  isInactive = false,
  onSave,
}: PriceListProductImageDialogProps) {
  const [open, setOpen] = useState(false)

  const [search, setSearch] = useState<string>(product?.produto ?? '')
  const [filters, setFilters] = useState<PriceListImageGalleryParams>({
    search: product?.produto,
  })

  const [selectedImage, setSelectedImage] = useState<Image>(image)
  const [isLoadingImage, setIsLoadingImage] = useState(false)

  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const { mutation } = useUpdatePriceListProduct(true)

  const {
    data,
    isFetching,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useImageGalleryInfinite(filters, open)

  const gallery = useMemo(
    () => (data ? data.pages.flatMap((cur) => cur.data) : []),
    [data],
  )

  function handleOpenChange(dialogOpen: boolean) {
    if (dialogOpen) {
      setSelectedImage(image)
      setSearch(product?.produto ?? '')
      setFilters({ search: product?.produto })
    }

    setOpen(dialogOpen)
  }

  function handleSearch(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.currentTarget.value

    setSearch(value)

    startTransition(() => {
      setFilters((prevFilters) => {
        if (value) {
          return {
            ...prevFilters,
            search: value,
          }
        }

        return removePropsFromObject(prevFilters, ['search'])
      })
    })
  }

  async function handleOnSave() {
    if (onSave) {
      onSave(selectedImage)
    } else {
      await mutation.mutateAsync({
        ...(product as UpdatePriceListProductBody),
        priceListId: priceListId as number,
        id: id as number,
        imagem: selectedImage?.imagem,
      })
    }

    handleOpenChange(false)
  }

  useEffect(() => {
    if (!open) return

    let observer: IntersectionObserver | null = null

    const rafId = requestAnimationFrame(() => {
      const rootElement = document.getElementById('gallery-scroll-content')
      const sentinelElement = sentinelRef.current

      if (!rootElement || !sentinelElement) return

      if (observer && sentinelRef.current) {
        observer.unobserve(sentinelRef.current)
      }

      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && hasNextPage) {
            fetchNextPage()
          }
        },
        {
          threshold: 1,
          root: rootElement,
          rootMargin: '0px',
        },
      )

      observer.observe(sentinelElement)
    })

    return () => {
      cancelAnimationFrame(rafId)
    }
  }, [fetchNextPage, hasNextPage, open])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {image?.imagem_path ? (
          <button
            className="group size-16 rounded ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:ring-offset-2 disabled:cursor-not-allowed"
            disabled={isInactive}
          >
            <img
              className={cn(
                'aspect-square size-16 rounded border border-sky-25',
                !isInactive &&
                  'sm:group-hover:border-sky-100 sm:group-active:border-sky-500',
              )}
              src={image.imagem_path}
              alt="Imagem de descrição do produto"
            />
          </button>
        ) : (
          <AddImageButton className={cn('mb-[5px]', className)} />
        )}
      </DialogTrigger>

      <DialogContent className="w-full max-w-[39.5rem] gap-8">
        <DialogHeader>
          <DialogTitle className="leading-normal">ADICIONAR IMAGEM</DialogTitle>
        </DialogHeader>

        <DialogDescription className="hidden" />

        <DialogBody className="flex flex-col gap-4">
          <ImportImage
            id={product?.id}
            currentImage={selectedImage}
            removeCurrentImage={() => setSelectedImage(null)}
            isLoading={isLoadingImage}
            setIsLoading={setIsLoadingImage}
            callbackFn={({ filename, path }) => {
              setSelectedImage({
                descricao: '',
                imagem: filename as string,
                imagem_path: path as string,
              })
            }}
          />

          <div className="flex flex-col gap-4">
            <h3>GALERIA DE IMAGENS</h3>

            <div className="relative flex flex-1 lg:max-w-80">
              <Search
                className="absolute left-2 top-3 shrink-0 opacity-50"
                size={16}
              />

              <Input
                className={cn('flex h-10 pl-8', isFetching && 'pr-8')}
                autoFocus
                value={search}
                onChange={handleSearch}
                type="search"
                placeholder="Pesquisar"
                autoComplete="off"
              />

              {isFetching && (
                <Spinner className="absolute right-2 top-3 size-4 shrink-0" />
              )}
            </div>

            <div
              id="gallery-scroll-content"
              className="h-60 overflow-y-auto rounded bg-gray-50 p-4"
            >
              {isLoading ? (
                <CenteredSpinner />
              ) : !gallery.length ? (
                <div className="grid h-full place-content-center text-sm text-gray-500">
                  <span>
                    Nenhuma imagem encontrada, tente alterar ou limpar a
                    pesquisa.
                  </span>
                </div>
              ) : (
                <div className="grid grid-cols-3 place-items-center gap-4 sm:flex sm:flex-wrap">
                  {gallery.map((image, index) => (
                    <button
                      className="group rounded ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:ring-offset-1"
                      key={`${image.descricao}-${index}`}
                      onClick={() => setSelectedImage(image)}
                    >
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Avatar
                              className={cn(
                                'size-16 rounded border border-sky-25 transition-transform duration-300 sm:group-hover:scale-110 sm:group-hover:border-sky-100 sm:group-active:border-sky-500',
                                selectedImage === image &&
                                  'border-2 border-sky-500',
                              )}
                            >
                              <AvatarImage
                                src={image.imagem_path}
                                alt={image.descricao}
                                loading="lazy"
                              />

                              <AvatarFallback className="size-16 bg-transparent">
                                <ImageIcon className="text-gray-200" />
                              </AvatarFallback>
                            </Avatar>
                          </TooltipTrigger>
                          <TooltipPortal>
                            <TooltipContent>{image.descricao}</TooltipContent>
                          </TooltipPortal>
                        </Tooltip>
                      </TooltipProvider>
                    </button>
                  ))}

                  {isFetchingNextPage && (
                    <GalleryLoading totalItems={gallery.length} />
                  )}

                  <div ref={sentinelRef} className="h-1" />
                </div>
              )}
            </div>
          </div>

          <span className="text-xs text-gray-500">
            Tipos de imagens permitidos: jpeg e png
          </span>

          <footer className="flex flex-col-reverse items-center gap-2 sm:ml-auto sm:flex-row">
            <Button
              type="button"
              className="max-sm:w-full"
              variant="tertiary"
              disabled={mutation.isPending || isLoadingImage}
              onClick={() => handleOpenChange(false)}
            >
              CANCELAR
            </Button>

            <Button
              className="w-full sm:w-[7.25rem]"
              type="submit"
              variant="primary"
              onClick={handleOnSave}
              isLoading={mutation.isPending || isLoadingImage}
              disabled={mutation.isPending || isLoadingImage}
            >
              SALVAR
            </Button>
          </footer>
        </DialogBody>

        <DialogDismiss className="absolute right-6 top-6" />
      </DialogContent>
    </Dialog>
  )
}
